import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';

import { EndpointFactory } from './endpoint-factory.service';
import { ConfigurationService } from './configuration.service';

@Injectable()

export class UserAccessEndpoint extends EndpointFactory {
  private readonly getUserAccessesEndpointPath: string = "/api/userAccessService/getUserAccesses";
  private readonly getUserAccessesByAppEndpointPath: string = "/api/userAccessService/getUserAccessesByApp";
  private readonly getUserAccessEndpointPath: string = "/api/userAccessService/getUserAccess";
  private readonly getDepartmentsEndpointPath: string = "/api/userAccessService/getDepartments";
  private readonly getAgenciesEndpointPath: string = "/api/userAccessService/getAgencies";
  private readonly getRolesEndpointPath: string = "/api/userAccessService/getRoles";
  private readonly getRolesByAppEndpointPath: string = "/api/userAccessService/getRolesByApp";
  private readonly getVisibleUserProfilesEndpointPath: string = "/api/userAccessService/getVisibleUserProfiles";
  private readonly createNewUserAccessEndpointPath: string = "/api/userAccessService/createNewAccessItem";
  private readonly createNewUserProfileEndpointPath: string = "/api/userAccessService/createNewUserProfile";
  private readonly deleteUserAccessEndpointPath: string = "/api/userAccessService/deleteUserAccess";
  private readonly submitUserAccessEndpointPath: string = "/api/userAccessService/submitUserAccess";
  private readonly deactivateUserAccessEndpointPath: string = "/api/userAccessService/deactivateUserAccess";
  private readonly activateUserAccessEndpointPath: string = "/api/userAccessService/activateUserAccess";
  private readonly unlockWebAccountEndpointPath: string = "/api/userAccessService/unlockWebAccount";

  get getUserAccessesEndpointUrl() { return this.configurations.baseUrl + this.getUserAccessesEndpointPath; }
  get getUserAccessesByAppEndpointUrl() { return this.configurations.baseUrl + this.getUserAccessesByAppEndpointPath; }
  get getDepartmentsEndpointUrl() { return this.configurations.baseUrl + this.getDepartmentsEndpointPath; }
  get getAgenciesEndpointUrl() { return this.configurations.baseUrl + this.getAgenciesEndpointPath; }
  get getUserAccessEndpointUrl() { return this.configurations.baseUrl + this.getUserAccessEndpointPath; }
  get getVisibleUserProfilesEndpointUrl() { return this.configurations.baseUrl + this.getVisibleUserProfilesEndpointPath; }
  get getRolesEndpointUrl() { return this.configurations.baseUrl + this.getRolesEndpointPath; }
  get getRolesByAppEndpointUrl() { return this.configurations.baseUrl + this.getRolesByAppEndpointPath; }
  get createNewUserAccessEndpointUrl() { return this.configurations.baseUrl + this.createNewUserAccessEndpointPath; }
  get createNewUserProfileEndpointUrl() { return this.configurations.baseUrl + this.createNewUserProfileEndpointPath; }
  get deleteUserAccessEndpointUrl() { return this.configurations.baseUrl + this.deleteUserAccessEndpointPath; }
  get submitUserAccessEndpointUrl() { return this.configurations.baseUrl + this.submitUserAccessEndpointPath; }
  get deactivateUserAccessEndpointUrl() { return this.configurations.baseUrl + this.deactivateUserAccessEndpointPath; }
  get activateUserAccessEndpointUrl() { return this.configurations.baseUrl + this.activateUserAccessEndpointPath; }
  get unlockWebAccountEndpointUrl() { return this.configurations.baseUrl + this.unlockWebAccountEndpointPath; }
  
  constructor(http: HttpClient, configurations: ConfigurationService, injector: Injector) {
    super(http, configurations, injector);
  }

  getDepartments<T>(userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.getDepartmentsEndpointUrl}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getAgencies<T>(departmentId?: string, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.getAgenciesEndpointUrl}/${departmentId}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getUserAccessesByApp<T>(applicationVldID: number, departmentId?: string, agencyId?: string, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.getUserAccessesByAppEndpointUrl}/${applicationVldID}/${departmentId}/${agencyId}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getUserAccesses<T>(departmentId?: string, agencyId?: string, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.getUserAccessesEndpointUrl}/${departmentId}/${agencyId}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getUserAccess<T>(accessId?: string, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.getUserAccessEndpointUrl}/${accessId}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getRoles<T>(userProfileId: string) {
    let endpointUrl = `${this.getRolesEndpointUrl}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getRolesByApp<T>(applicationVldID: number, userProfileId: string) {
    let endpointUrl = `${this.getRolesByAppEndpointUrl}/${applicationVldID}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  getVisibleUserProfiles<T>(userProfileId?: string) {
    let endpointUrl = `${this.getVisibleUserProfilesEndpointUrl}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  createNewAccessItem<T>(userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.createNewUserAccessEndpointUrl}/${userProfileId}`;
    return this.http.get<T>(endpointUrl);
  }

  createNewUserProfile<T>() {
    let endpointUrl = `${this.createNewUserProfileEndpointUrl}/`;
    return this.http.get<T>(endpointUrl);
  }

  deleteUserAccess<T>(accessId?: string, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.deleteUserAccessEndpointUrl}/`;

    const body = {
      userAccessId: accessId,
      userProfileId: userProfileId
    };

    return this.http.post<T>(endpointUrl, JSON.stringify(body), this.getRequestHeaders());
  }

  submitUserAccess<T>(accessItem: any, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.submitUserAccessEndpointUrl}/`;
    const body = {
      accessItem: accessItem,
      userProfileId: userProfileId
    };
    return this.http.post<T>(endpointUrl, JSON.stringify(body), this.getRequestHeaders());
  }

  deactivateUserAccess<T>(accessItem: any, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.deactivateUserAccessEndpointUrl}/`;
    const body = {
      accessItem: accessItem,
      userProfileId: userProfileId
    };
    return this.http.post<T>(endpointUrl, JSON.stringify(body), this.getRequestHeaders());
  }

  activateUserAccess<T>(accessItem: any, userProfileId?: string): Observable<T> {
    let endpointUrl = `${this.activateUserAccessEndpointUrl}/`;
    const body = {
      accessItem: accessItem,
      userProfileId: userProfileId
    };
    return this.http.post<T>(endpointUrl, JSON.stringify(body), this.getRequestHeaders());
  }
}
